import { SUPPLIER_TYPES, getSupplierTypes } from '../../utils/supplierTypes';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Supplier, SupplierContact, SupplierCatalogueProduct, suppliersAPI, SupplierProductCategory, SupplierProductType } from '../../services/api';
import { ArrowLeftIcon, TrashIcon, PlusIcon, UserGroupIcon, CubeIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { Card, CardBody, Button, Spinner } from '../ui';
import ExcelImportModal from '../common/ExcelImportModal';
import { ExcelImportRow, supplierCatalogueProductImportSchema } from '../../utils/excelImport';
import {
  createSupplierProductImportCheckpoint,
  runSupplierProductImport,
  SupplierProductImportCheckpoint,
  SupplierProductImportError,
  SupplierProductImportProgress,
} from '../../utils/supplierProductImport';

const SUGGESTED_ROLES = [
  'Sales Rep / Account Manager',
  'QC / Regulatory Contact',
  'Emergency Contact',
  'Customer Service',
];

const CATALOGUE_PRODUCTS_PAGE_SIZE = 25;

interface ContactFormData {
  name: string;
  role: string;
  email: string;
  phone: string;
}

interface CatalogueProductFormData {
  typeId: string;
  importTypeName?: string;
  sku: string;
  name: string;
  categoryId: string;
  importCategoryName?: string;
  cost: string;
  leadTime: string;
  importRowNumber?: number;
}

interface PendingNewSupplierImport {
  supplierId: number;
  checkpoint: SupplierProductImportCheckpoint;
}

const SupplierForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    types: ['MANUFACTURER'] as Supplier['type'][],
    customType: '',
    status: 'ACTIVE_SUPPLIER' as Supplier['status'],
    emails: [''],
    websites: [''],
    phoneNumbers: [''],
    address: '',
    importance: 'MEDIUM' as Supplier['importance'],
    notes: '',
  });

  const [contacts, setContacts] = useState<ContactFormData[]>([]);
  const [catalogueProducts, setCatalogueProducts] = useState<CatalogueProductFormData[]>([]);
  const [catalogueProductsPage, setCatalogueProductsPage] = useState(1);
  const [categories, setCategories] = useState<SupplierProductCategory[]>([]);
  const [productTypes, setProductTypes] = useState<SupplierProductType[]>([]);
  const [showNewCategory, setShowNewCategory] = useState<number | null>(null);
  const [showEditCategory, setShowEditCategory] = useState<number | null>(null);
  const [showNewType, setShowNewType] = useState<number | null>(null);
  const [showEditType, setShowEditType] = useState<number | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editCategoryName, setEditCategoryName] = useState('');
  const [newTypeName, setNewTypeName] = useState('');
  const [editTypeName, setEditTypeName] = useState('');
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const [isCreatingType, setIsCreatingType] = useState(false);
  const [isEditingType, setIsEditingType] = useState(false);
  const [deletingTypeId, setDeletingTypeId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showProductImportModal, setShowProductImportModal] = useState(false);
  const [catalogueProductsDirty, setCatalogueProductsDirty] = useState(false);
  const [productImportProgress, setProductImportProgress] = useState<SupplierProductImportProgress | null>(null);
  const [pendingNewSupplierImport, setPendingNewSupplierImport] = useState<PendingNewSupplierImport | null>(null);
  const pendingNewSupplierImportRef = useRef<PendingNewSupplierImport | null>(null);
  const submitInFlightRef = useRef(false);
  const existingSupplierImportRef = useRef<{
    rows: ExcelImportRow[];
    checkpoint: SupplierProductImportCheckpoint;
  } | null>(null);

  useEffect(() => {
    fetchCategories();
    fetchProductTypes();
    if (isEdit && id) {
      fetchSupplier(parseInt(id));
    }
  }, [id, isEdit]);

  useEffect(() => {
    const pageCount = Math.max(1, Math.ceil(catalogueProducts.length / CATALOGUE_PRODUCTS_PAGE_SIZE));
    setCatalogueProductsPage((page) => Math.min(Math.max(page, 1), pageCount));
  }, [catalogueProducts.length]);

  const fetchCategories = async () => {
    try {
      const data = await suppliersAPI.getCategories();
      setCategories(data);
    } catch (err) {
      console.error('Failed to fetch supplier categories', err);
    }
  };

  const fetchProductTypes = async () => {
    try {
      const data = await suppliersAPI.getProductTypes(true);
      setProductTypes(data);
    } catch (err) {
      console.error('Failed to fetch supplier product types', err);
    }
  };

  const normalizeProductTypeKey = (value: string) =>
    value
      .trim()
      .toUpperCase()
      .replace(/&/g, 'AND')
      .replace(/\s+/g, '_')
      .replace(/-/g, '_')
      .replace(/_+/g, '_');

  const findMatchingProductType = (types: SupplierProductType[], rawValue: string) => {
    const normalizedValue = normalizeProductTypeKey(rawValue);
    const legacyAliases: Record<string, string> = {
      OILS_AND_OILS_SEEDS: 'OILS_AND_OIL_SEEDS',
    };
    const resolvedLegacyKey = legacyAliases[normalizedValue] || normalizedValue;
    const trimmedValue = rawValue.trim().toLowerCase();

    return types.find((type) =>
      type.legacyKey === resolvedLegacyKey || type.name.trim().toLowerCase() === trimmedValue,
    );
  };

  const getProductTypeOptionLabel = (productType: SupplierProductType) =>
    `${productType.name}${productType.isActive ? '' : ' (Archived)'}`;

  const handleCreateCategory = async (index: number) => {
    if (!newCategoryName.trim()) return;
    
    try {
      setIsCreatingCategory(true);
      const newCategory = await suppliersAPI.createCategory({ name: newCategoryName });
      
      const data = await suppliersAPI.getCategories();
      setCategories(data);
      
      updateCatalogueProduct(index, 'categoryId', newCategory.id.toString());
      
      setNewCategoryName('');
      setShowNewCategory(null);
    } catch (err: any) {
      console.error('Failed to create category', err);
      alert(err.response?.data?.message || 'Failed to create category');
    } finally {
      setIsCreatingCategory(false);
    }
  };

  const handleEditCategory = async (index: number, categoryId: number) => {
    if (!editCategoryName.trim()) return;
    
    try {
      setIsEditingCategory(true);
      await suppliersAPI.updateCategory(categoryId, { name: editCategoryName });
      
      const data = await suppliersAPI.getCategories();
      setCategories(data);
      
      setEditCategoryName('');
      setShowEditCategory(null);
    } catch (err: any) {
      console.error('Failed to edit category', err);
      alert(err.response?.data?.message || 'Failed to edit category');
    } finally {
      setIsEditingCategory(false);
    }
  };

  const handleCreateProductType = async (index: number) => {
    if (!newTypeName.trim()) return;

    try {
      setIsCreatingType(true);
      const newType = await suppliersAPI.createProductType({ name: newTypeName.trim() });

      const data = await suppliersAPI.getProductTypes(true);
      setProductTypes(data);

      updateCatalogueProduct(index, 'typeId', newType.id.toString());

      setNewTypeName('');
      setShowNewType(null);
    } catch (err: any) {
      console.error('Failed to create product type', err);
      alert(err.response?.data?.message || 'Failed to create product type');
    } finally {
      setIsCreatingType(false);
    }
  };

  const handleEditProductType = async (typeId: number) => {
    if (!editTypeName.trim()) return;

    try {
      setIsEditingType(true);
      await suppliersAPI.updateProductType(typeId, { name: editTypeName.trim() });

      const data = await suppliersAPI.getProductTypes(true);
      setProductTypes(data);

      setEditTypeName('');
      setShowEditType(null);
    } catch (err: any) {
      console.error('Failed to edit product type', err);
      alert(err.response?.data?.message || 'Failed to edit product type');
    } finally {
      setIsEditingType(false);
    }
  };

  const handleDeleteProductType = async (index: number, typeId: number) => {
    if (!window.confirm('Are you sure you want to remove this product type? If it is used by existing products, it will be archived instead.')) {
      return;
    }

    try {
      setDeletingTypeId(typeId);
      const result = await suppliersAPI.deleteProductType(typeId);
      const data = await suppliersAPI.getProductTypes(true);
      setProductTypes(data);

      if (result?.deleted) {
        const selectedTypeId = catalogueProducts[index]?.typeId;
        if (selectedTypeId === typeId.toString()) {
          updateCatalogueProduct(index, 'typeId', '');
        }
      }
    } catch (err: any) {
      console.error('Failed to remove product type', err);
      alert(err.response?.data?.message || 'Failed to remove product type');
    } finally {
      setDeletingTypeId(null);
    }
  };

  const fetchSupplier = async (supplierId: number) => {
    try {
      setLoading(true);
      const supplier = await suppliersAPI.getById(supplierId);
      setFormData({
        name: supplier.name,
        types: getSupplierTypes(supplier),
        customType: supplier.customType || '',
        status: supplier.status || 'ACTIVE_SUPPLIER',
        emails: supplier.emails?.length > 0 ? supplier.emails : [''],
        websites: supplier.websites?.length > 0 ? supplier.websites : [''],
        phoneNumbers: supplier.phoneNumbers.length > 0 ? supplier.phoneNumbers : [''],
        address: supplier.address || '',
        importance: supplier.importance,
        notes: supplier.notes || '',
      });
      if (supplier.contacts && supplier.contacts.length > 0) {
        setContacts(supplier.contacts.map((c: SupplierContact) => ({
          name: c.name,
          role: c.role,
          email: c.email || '',
          phone: c.phone || '',
        })));
      }
      setCatalogueProducts((supplier.catalogueProducts || []).map((cp: SupplierCatalogueProduct) => ({
        typeId: cp.typeId?.toString() || '',
        sku: cp.sku || '',
        name: cp.name,
        categoryId: cp.categoryId?.toString() || '',
        cost: cp.cost?.toString() || '',
        leadTime: cp.leadTime || '',
      })));
      setCatalogueProductsDirty(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch supplier');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitInFlightRef.current) return;

    submitInFlightRef.current = true;
    setLoading(true);
    setError('');

    try {
      const catalogueProductPayload = catalogueProducts.filter(cp => cp.name.trim() !== '').map(cp => ({
        __rowNumber: cp.importRowNumber,
        typeId: parseInt(cp.typeId) || undefined,
        type: cp.importTypeName || undefined,
        sku: cp.sku || undefined,
        name: cp.name,
        categoryId: parseInt(cp.categoryId) || undefined,
        categoryName: cp.importCategoryName || undefined,
        cost: cp.cost ? parseFloat(cp.cost) : undefined,
        leadTime: cp.leadTime || undefined,
      }));
      if (formData.types.length === 0) throw new Error('Select at least one supplier type');
      const dataToSubmit: any = {
        ...formData,
        emails: formData.emails.filter(e => e.trim() !== ''),
        websites: formData.websites.filter(w => w.trim() !== ''),
        phoneNumbers: formData.phoneNumbers.filter(p => p.trim() !== ''),
        customType: formData.types.includes('OTHER') ? formData.customType : undefined,
        contacts: contacts.filter(c => c.name.trim() !== '').map(c => ({
          name: c.name,
          role: c.role,
          email: c.email || undefined,
          phone: c.phone || undefined,
        })),
      };

      if (isEdit && id) {
        if (catalogueProductsDirty) {
          dataToSubmit.catalogueProducts = catalogueProductPayload;
        }
        await suppliersAPI.update(parseInt(id), dataToSubmit);
        navigate('/admin/suppliers');
      } else {
        let pendingImport = pendingNewSupplierImportRef.current || pendingNewSupplierImport;

        if (!pendingImport) {
          const createdSupplier = await suppliersAPI.create(dataToSubmit);
          if (catalogueProductPayload.length === 0) {
            navigate('/admin/suppliers');
            return;
          }

          pendingImport = {
            supplierId: createdSupplier.id,
            checkpoint: createSupplierProductImportCheckpoint(catalogueProductPayload),
          };
          pendingNewSupplierImportRef.current = pendingImport;
          setPendingNewSupplierImport(pendingImport);
        }

        const totalCount = pendingImport.checkpoint.batches.reduce((total, batch) => total + batch.length, 0);
        setProductImportProgress({
          processedCount: pendingImport.checkpoint.processedCount,
          totalCount,
          importedCount: pendingImport.checkpoint.importedCount,
          skippedCount: pendingImport.checkpoint.skippedCount,
          batchIndex: pendingImport.checkpoint.nextBatchIndex,
          batchCount: pendingImport.checkpoint.batches.length,
        });

        try {
          const completedCheckpoint = await runSupplierProductImport(
            pendingImport.supplierId,
            pendingImport.checkpoint,
            (progress, checkpoint) => {
              setProductImportProgress(progress);
              const nextPendingImport = { supplierId: pendingImport!.supplierId, checkpoint };
              pendingNewSupplierImportRef.current = nextPendingImport;
              setPendingNewSupplierImport(nextPendingImport);
            },
          );
          const completedImport = { supplierId: pendingImport.supplierId, checkpoint: completedCheckpoint };
          pendingNewSupplierImportRef.current = completedImport;
          setPendingNewSupplierImport(completedImport);
          navigate('/admin/suppliers');
        } catch (importError) {
          if (importError instanceof SupplierProductImportError) {
            const resumableImport = { supplierId: pendingImport.supplierId, checkpoint: importError.checkpoint };
            pendingNewSupplierImportRef.current = resumableImport;
            setPendingNewSupplierImport(resumableImport);
          }
          throw importError;
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || `Failed to ${isEdit ? 'update' : 'create'} supplier`);
    } finally {
      submitInFlightRef.current = false;
      setLoading(false);
    }
  };

  // Phone handlers
  const handlePhoneChange = (index: number, value: string) => {
    const newPhones = [...formData.phoneNumbers];
    newPhones[index] = value;
    setFormData({ ...formData, phoneNumbers: newPhones });
  };

  const addPhone = () => {
    setFormData({ ...formData, phoneNumbers: [...formData.phoneNumbers, ''] });
  };

  const removePhone = (index: number) => {
    const newPhones = formData.phoneNumbers.filter((_, i) => i !== index);
    setFormData({ ...formData, phoneNumbers: newPhones.length > 0 ? newPhones : [''] });
  };

  // Email handlers
  const handleEmailChange = (index: number, value: string) => {
    const newEmails = [...formData.emails];
    newEmails[index] = value;
    setFormData({ ...formData, emails: newEmails });
  };

  const addEmail = () => {
    setFormData({ ...formData, emails: [...formData.emails, ''] });
  };

  const removeEmail = (index: number) => {
    const newEmails = formData.emails.filter((_, i) => i !== index);
    setFormData({ ...formData, emails: newEmails.length > 0 ? newEmails : [''] });
  };

  // Website handlers
  const handleWebsiteChange = (index: number, value: string) => {
    const newWebsites = [...formData.websites];
    newWebsites[index] = value;
    setFormData({ ...formData, websites: newWebsites });
  };

  const addWebsite = () => {
    setFormData({ ...formData, websites: [...formData.websites, ''] });
  };

  const removeWebsite = (index: number) => {
    const newWebsites = formData.websites.filter((_, i) => i !== index);
    setFormData({ ...formData, websites: newWebsites.length > 0 ? newWebsites : [''] });
  };

  // Contact handlers
  const addContact = () => {
    setContacts([...contacts, { name: '', role: '', email: '', phone: '' }]);
  };

  const updateContact = (index: number, field: keyof ContactFormData, value: string) => {
    const updated = [...contacts];
    updated[index] = { ...updated[index], [field]: value };
    setContacts(updated);
  };

  const removeContact = (index: number) => {
    setContacts(contacts.filter((_, i) => i !== index));
  };

  const addCatalogueProduct = () => {
    const nextCount = catalogueProducts.length + 1;
    setCatalogueProducts([...catalogueProducts, { typeId: '', sku: '', name: '', categoryId: '', cost: '', leadTime: '' }]);
    setCatalogueProductsDirty(true);
    setCatalogueProductsPage(Math.ceil(nextCount / CATALOGUE_PRODUCTS_PAGE_SIZE));
  };

  const updateCatalogueProduct = (index: number, field: keyof CatalogueProductFormData, value: string) => {
    const updated = [...catalogueProducts];
    updated[index] = {
      ...updated[index],
      [field]: value,
      ...(field === 'typeId' ? { importTypeName: undefined } : {}),
      ...(field === 'categoryId' ? { importCategoryName: undefined } : {}),
    };
    setCatalogueProducts(updated);
    setCatalogueProductsDirty(true);
  };

  const removeCatalogueProduct = (index: number) => {
    setCatalogueProducts(catalogueProducts.filter((_, i) => i !== index));
    setCatalogueProductsDirty(true);
  };

  const handleImportCatalogueProducts = async (rows: ExcelImportRow[]) => {
    if (isEdit && id) {
      let importSession = existingSupplierImportRef.current;
      if (!importSession || importSession.rows !== rows) {
        importSession = {
          rows,
          checkpoint: createSupplierProductImportCheckpoint(rows),
        };
        existingSupplierImportRef.current = importSession;
      }

      const totalCount = importSession.checkpoint.batches.reduce((total, batch) => total + batch.length, 0);
      setProductImportProgress({
        processedCount: importSession.checkpoint.processedCount,
        totalCount,
        importedCount: importSession.checkpoint.importedCount,
        skippedCount: importSession.checkpoint.skippedCount,
        batchIndex: importSession.checkpoint.nextBatchIndex,
        batchCount: importSession.checkpoint.batches.length,
      });

      try {
        const completedCheckpoint = await runSupplierProductImport(
          parseInt(id),
          importSession.checkpoint,
          (progress, checkpoint) => {
            setProductImportProgress(progress);
            existingSupplierImportRef.current = { rows, checkpoint };
          },
        );
        existingSupplierImportRef.current = { rows, checkpoint: completedCheckpoint };
        await fetchCategories();
        await fetchProductTypes();
        await fetchSupplier(parseInt(id));
        existingSupplierImportRef.current = null;
        return;
      } catch (importError) {
        if (importError instanceof SupplierProductImportError) {
          existingSupplierImportRef.current = { rows, checkpoint: importError.checkpoint };
        }
        throw importError;
      }
    }

    const importedProducts: CatalogueProductFormData[] = rows.map((row, index) => {
      const suppliedCategoryId = row.categoryId ? String(row.categoryId) : '';
      const categoryName = String(row.categoryName || 'Dummy').trim();
      const matchingCategory = suppliedCategoryId
        ? categories.find((item) => item.id.toString() === suppliedCategoryId)
        : categories.find((item) => item.name.trim().toLowerCase() === categoryName.toLowerCase());
      const suppliedTypeId = row.typeId ? String(row.typeId) : '';
      const typeName = String(row.type || 'Product').trim();
      const matchingProductType = suppliedTypeId
        ? productTypes.find((item) => item.id.toString() === suppliedTypeId)
        : findMatchingProductType(productTypes, typeName);

      return {
        typeId: matchingProductType?.id.toString() || suppliedTypeId,
        importTypeName: typeName,
        sku: row.sku ? String(row.sku) : '',
        name: row.name ? String(row.name) : '',
        categoryId: matchingCategory?.id.toString() || suppliedCategoryId,
        importCategoryName: categoryName,
        cost: row.cost === null || row.cost === undefined ? '' : String(row.cost),
        leadTime: row.leadTime ? String(row.leadTime) : '',
        importRowNumber: Number(row.__rowNumber) || index + 2,
      };
    });

    setCatalogueProducts((current) => [...current, ...importedProducts]);
    setCatalogueProductsDirty(true);
    setCatalogueProductsPage(Math.max(1, Math.ceil((catalogueProducts.length + importedProducts.length) / CATALOGUE_PRODUCTS_PAGE_SIZE)));
  };

  const catalogueProductsPageCount = Math.max(1, Math.ceil(catalogueProducts.length / CATALOGUE_PRODUCTS_PAGE_SIZE));
  const catalogueProductsStart = (catalogueProductsPage - 1) * CATALOGUE_PRODUCTS_PAGE_SIZE;
  const catalogueProductsEnd = Math.min(catalogueProductsStart + CATALOGUE_PRODUCTS_PAGE_SIZE, catalogueProducts.length);
  const visibleCatalogueProducts = useMemo(
    () => catalogueProducts.slice(catalogueProductsStart, catalogueProductsEnd),
    [catalogueProducts, catalogueProductsStart, catalogueProductsEnd],
  );

  if (loading && isEdit) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const inputClass = "w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500";
  const labelClass = "block text-sm font-medium text-neutral-700 mb-1";
  const productImportProgressText = productImportProgress
    ? `Imported ${productImportProgress.importedCount} of ${productImportProgress.totalCount} products` +
      (productImportProgress.skippedCount > 0 ? `; skipped ${productImportProgress.skippedCount}` : '') + '.'
    : '';

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/suppliers')}
          className="inline-flex items-center text-sm text-neutral-600 hover:text-neutral-900"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Suppliers
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <fieldset disabled={Boolean(pendingNewSupplierImport)} className="space-y-6">
          {/* Basic Info */}
          <Card variant="elevated">
          <CardBody className="p-6">
            <h2 className="text-2xl font-bold text-neutral-900 mb-6">
              {isEdit ? 'Edit Supplier' : 'Add New Supplier'}
            </h2>

            {error && (
              <div className="mb-4 bg-error-50 border border-error-200 text-error-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {!isEdit && productImportProgressText && (
              <div className="mb-4 bg-primary-50 border border-primary-200 text-primary-700 px-4 py-3 rounded">
                {productImportProgressText}
                {pendingNewSupplierImport && productImportProgress && productImportProgress.processedCount < productImportProgress.totalCount && (
                  <span> The supplier has been created; retry will continue without creating it again.</span>
                )}
              </div>
            )}

            <div className="space-y-6">
              <div>
                <label className={labelClass}>
                  Name <span className="text-error-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={inputClass}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>
                    Types <span className="text-error-500">*</span>
                  </label>
                  <fieldset aria-label="Supplier types" className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {SUPPLIER_TYPES.map(t => (
                      <label key={t.value} className="flex items-center gap-2 text-sm py-1">
                        <input type="checkbox" checked={formData.types.includes(t.value)}
                          onChange={(e) => setFormData(previous => ({ ...previous,
                            types: e.target.checked ? [...previous.types, t.value] : previous.types.filter(type => type !== t.value),
                          }))} />
                        {t.label}
                      </label>
                    ))}
                  </fieldset>
                  <p className="text-sm text-neutral-500 mt-2">Select all types that apply. At least one is required.</p>
                </div>

                {formData.types.includes('OTHER') && (
                  <div>
                    <label className={labelClass}>
                      Custom Type <span className="text-error-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.customType}
                      onChange={(e) => setFormData({ ...formData, customType: e.target.value })}
                      className={inputClass}
                      placeholder="Enter custom supplier type"
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as Supplier['status'] })}
                    className={inputClass}
                  >
                    <option value="ACTIVE_SUPPLIER">Supplier</option>
                    <option value="POTENTIAL_SUPPLIER">Potential Supplier</option>
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Importance</label>
                  <select
                    value={formData.importance}
                    onChange={(e) => setFormData({ ...formData, importance: e.target.value as Supplier['importance'] })}
                    className={inputClass}
                  >
                    <option value="VERY_HIGH">Very High</option>
                    <option value="HIGH">High</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="LOW">Low</option>
                    <option value="VERY_LOW">Very Low</option>
                  </select>
                </div>
              </div>

              <div>
                <label className={labelClass}>Emails</label>
                {formData.emails.map((email, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => handleEmailChange(index, e.target.value)}
                      className={`flex-1 ${inputClass}`}
                      placeholder="Email address"
                    />
                    {formData.emails.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => removeEmail(index)}
                        leftIcon={<TrashIcon className="h-4 w-4" />}
                        className="text-error-600 hover:text-error-800"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addEmail}
                  className="text-sm text-primary-600 hover:text-primary-800"
                >
                  + Add another email
                </button>
              </div>

              <div>
                <label className={labelClass}>Website Urls</label>
                {formData.websites.map((website, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="url"
                      value={website}
                      onChange={(e) => handleWebsiteChange(index, e.target.value)}
                      className={`flex-1 ${inputClass}`}
                      placeholder="https://example.com"
                    />
                    {formData.websites.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => removeWebsite(index)}
                        leftIcon={<TrashIcon className="h-4 w-4" />}
                        className="text-error-600 hover:text-error-800"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addWebsite}
                  className="text-sm text-primary-600 hover:text-primary-800"
                >
                  + Add another website
                </button>
              </div>

              <div>
                <label className={labelClass}>Phone Numbers</label>
                {formData.phoneNumbers.map((phone, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => handlePhoneChange(index, e.target.value)}
                      className={`flex-1 ${inputClass}`}
                      placeholder="Phone number"
                    />
                    {formData.phoneNumbers.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => removePhone(index)}
                        leftIcon={<TrashIcon className="h-4 w-4" />}
                        className="text-error-600 hover:text-error-800"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addPhone}
                  className="text-sm text-primary-600 hover:text-primary-800"
                >
                  + Add another phone
                </button>
              </div>

              <div>
                <label className={labelClass}>Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  rows={3}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={4}
                  className={inputClass}
                />
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Contacts Section */}
        <Card variant="elevated">
          <CardBody className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <UserGroupIcon className="h-5 w-5 text-primary-600" />
                <h3 className="text-lg font-semibold text-neutral-900">People / Contacts</h3>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addContact}
                leftIcon={<PlusIcon className="h-4 w-4" />}
              >
                Add Contact
              </Button>
            </div>

            {contacts.length === 0 ? (
              <p className="text-sm text-neutral-500 text-center py-4">
                No contacts added yet. Click "Add Contact" to add people.
              </p>
            ) : (
              <div className="space-y-4">
                {contacts.map((contact, index) => (
                  <div key={index} className="border border-neutral-200 rounded-lg p-4 bg-neutral-50">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-neutral-700">Contact #{index + 1}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeContact(index)}
                        className="text-error-600 hover:text-error-800"
                        leftIcon={<TrashIcon className="h-4 w-4" />}
                      >
                        Remove
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className={labelClass}>Name <span className="text-error-500">*</span></label>
                        <input
                          type="text"
                          required
                          value={contact.name}
                          onChange={(e) => updateContact(index, 'name', e.target.value)}
                          className={inputClass}
                          placeholder="Contact name"
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Role <span className="text-error-500">*</span></label>
                        <input
                          type="text"
                          required
                          list={`role-suggestions-${index}`}
                          value={contact.role}
                          onChange={(e) => updateContact(index, 'role', e.target.value)}
                          className={inputClass}
                          placeholder="e.g. Sales Rep, QC Contact"
                        />
                        <datalist id={`role-suggestions-${index}`}>
                          {SUGGESTED_ROLES.map(role => (
                            <option key={role} value={role} />
                          ))}
                        </datalist>
                      </div>
                      <div>
                        <label className={labelClass}>Email</label>
                        <input
                          type="email"
                          value={contact.email}
                          onChange={(e) => updateContact(index, 'email', e.target.value)}
                          className={inputClass}
                          placeholder="contact@example.com"
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Phone</label>
                        <input
                          type="tel"
                          value={contact.phone}
                          onChange={(e) => updateContact(index, 'phone', e.target.value)}
                          className={inputClass}
                          placeholder="Phone number"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Catalogue Products Section */}
        <Card variant="elevated">
          <CardBody className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <CubeIcon className="h-5 w-5 text-primary-600" />
                <h3 className="text-lg font-semibold text-neutral-900">Catalogue Products</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowProductImportModal(true)}
                  leftIcon={<ArrowUpTrayIcon className="h-4 w-4" />}
                >
                  Import Excel
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addCatalogueProduct}
                  leftIcon={<PlusIcon className="h-4 w-4" />}
                >
                  Add Product
                </Button>
              </div>
            </div>

            {catalogueProducts.length === 0 ? (
              <p className="text-sm text-neutral-500 text-center py-4">
                No catalogue products added yet. Click "Add Product" to add items.
              </p>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-col gap-3 rounded-lg border border-neutral-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-neutral-600">
                    Showing <span className="font-medium text-neutral-900">{catalogueProductsStart + 1}</span> to{' '}
                    <span className="font-medium text-neutral-900">{catalogueProductsEnd}</span> of{' '}
                    <span className="font-medium text-neutral-900">{catalogueProducts.length}</span> products
                  </div>
                  {catalogueProductsPageCount > 1 && (
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setCatalogueProductsPage((page) => Math.max(1, page - 1))}
                        disabled={catalogueProductsPage === 1}
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-neutral-500">
                        Page {catalogueProductsPage} of {catalogueProductsPageCount}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setCatalogueProductsPage((page) => Math.min(catalogueProductsPageCount, page + 1))}
                        disabled={catalogueProductsPage === catalogueProductsPageCount}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>

                {visibleCatalogueProducts.map((product, visibleIndex) => {
                  const index = catalogueProductsStart + visibleIndex;
                  const selectedProductTypeExists = productTypes.some((type) => type.id.toString() === product.typeId);
                  const selectedCategoryExists = categories.some((category) => category.id.toString() === product.categoryId);
                  return (
                  <div key={index} className="border border-neutral-200 rounded-lg p-4 bg-neutral-50">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-neutral-700">Product #{index + 1}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCatalogueProduct(index)}
                        className="text-error-600 hover:text-error-800"
                        leftIcon={<TrashIcon className="h-4 w-4" />}
                      >
                        Remove
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <label className={labelClass}>Types <span className="text-error-500">*</span></label>
                          <div className="flex space-x-2">
                            {product.typeId && showEditType !== index && (
                              <button
                                type="button"
                                onClick={() => {
                                  setShowNewType(null);
                                  setShowEditType(showEditType === index ? null : index);
                                  const productType = productTypes.find((type) => type.id.toString() === product.typeId);
                                  if (productType) {
                                    setEditTypeName(productType.name);
                                  }
                                }}
                                className="text-xs text-primary-600 hover:text-primary-800"
                              >
                                Edit
                              </button>
                            )}
                            {product.typeId && (
                              <button
                                type="button"
                                onClick={() => handleDeleteProductType(index, parseInt(product.typeId))}
                                className="text-xs text-error-600 hover:text-error-800"
                                disabled={deletingTypeId === parseInt(product.typeId)}
                              >
                                {deletingTypeId === parseInt(product.typeId) ? 'Removing...' : 'Remove'}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                setShowEditType(null);
                                setShowNewType(showNewType === index ? null : index);
                              }}
                              className="text-xs text-primary-600 hover:text-primary-800"
                            >
                              {showNewType === index ? 'Cancel' : '+ New'}
                            </button>
                          </div>
                        </div>
                        {showNewType === index ? (
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={newTypeName}
                              onChange={(e) => setNewTypeName(e.target.value)}
                              className="flex-1 block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                              placeholder="New type name"
                              autoFocus
                            />
                            <Button
                              type="button"
                              onClick={() => handleCreateProductType(index)}
                              isLoading={isCreatingType}
                              disabled={!newTypeName.trim()}
                              size="sm"
                            >
                              Add
                            </Button>
                          </div>
                        ) : showEditType === index && product.typeId ? (
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={editTypeName}
                              onChange={(e) => setEditTypeName(e.target.value)}
                              className="flex-1 block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                              placeholder="Edit type name"
                              autoFocus
                            />
                            <Button
                              type="button"
                              onClick={() => handleEditProductType(parseInt(product.typeId))}
                              isLoading={isEditingType}
                              disabled={!editTypeName.trim()}
                              size="sm"
                            >
                              Save
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={() => setShowEditType(null)}
                              size="sm"
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <select
                            required={!product.importTypeName}
                            value={product.typeId}
                            onChange={(e) => updateCatalogueProduct(index, 'typeId', e.target.value)}
                            className={inputClass}
                          >
                            <option value="">
                              {product.importTypeName
                                ? `${product.importTypeName} (resolved during catalogue import)`
                                : 'Select a type'}
                            </option>
                            {product.typeId && !selectedProductTypeExists && (
                              <option value={product.typeId}>Type ID {product.typeId} (validated during import)</option>
                            )}
                            {productTypes.map((type) => (
                              <option key={type.id} value={type.id.toString()}>
                                {getProductTypeOptionLabel(type)}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                      <div>
                        <label className={labelClass}>SKU</label>
                        <input
                          type="text"
                          value={product.sku}
                          onChange={(e) => updateCatalogueProduct(index, 'sku', e.target.value)}
                          className={inputClass}
                          placeholder="SKU code"
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Name <span className="text-error-500">*</span></label>
                        <input
                          type="text"
                          required
                          value={product.name}
                          onChange={(e) => updateCatalogueProduct(index, 'name', e.target.value)}
                          className={inputClass}
                          placeholder="Product name"
                        />
                      </div>
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <label className="block text-sm font-medium text-neutral-700">Category <span className="text-error-500">*</span></label>
                          <div className="flex space-x-2">
                            {product.categoryId && showEditCategory !== index && (
                                <button
                                    type="button"
                                    onClick={() => {
                                      setShowNewCategory(null);
                                      setShowEditCategory(showEditCategory === index ? null : index);
                                      const category = categories.find(c => c.id.toString() === product.categoryId);
                                      if (category) {
                                          setEditCategoryName(category.name);
                                      }
                                    }}
                                    className="text-xs text-primary-600 hover:text-primary-800"
                                >
                                    Edit
                                </button>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                  setShowEditCategory(null);
                                  setShowNewCategory(showNewCategory === index ? null : index);
                              }}
                              className="text-xs text-primary-600 hover:text-primary-800"
                            >
                              {showNewCategory === index ? 'Cancel' : '+ New'}
                            </button>
                          </div>
                        </div>
                        {showNewCategory === index ? (
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={newCategoryName}
                              onChange={(e) => setNewCategoryName(e.target.value)}
                              className="flex-1 block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                              placeholder="New category name"
                              autoFocus
                            />
                            <Button
                              type="button"
                              onClick={() => handleCreateCategory(index)}
                              isLoading={isCreatingCategory}
                              disabled={!newCategoryName.trim()}
                              size="sm"
                            >
                              Add
                            </Button>
                          </div>
                        ) : showEditCategory === index && product.categoryId ? (
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={editCategoryName}
                                    onChange={(e) => setEditCategoryName(e.target.value)}
                                    className="flex-1 block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                    placeholder="Edit category name"
                                    autoFocus
                                />
                                <Button
                                    type="button"
                                    onClick={() => handleEditCategory(index, parseInt(product.categoryId))}
                                    isLoading={isEditingCategory}
                                    disabled={!editCategoryName.trim()}
                                    size="sm"
                                >
                                    Save
                                </Button>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() => setShowEditCategory(null)}
                                    size="sm"
                                >
                                    Cancel
                                </Button>
                            </div>
                        ) : (
                          <select
                            required={!product.importCategoryName}
                            value={product.categoryId}
                            onChange={(e) => updateCatalogueProduct(index, 'categoryId', e.target.value)}
                            className={inputClass}
                          >
                            <option value="">
                              {product.importCategoryName
                                ? `${product.importCategoryName} (resolved during catalogue import)`
                                : 'Select a category'}
                            </option>
                            {product.categoryId && !selectedCategoryExists && (
                              <option value={product.categoryId}>Category ID {product.categoryId} (validated during import)</option>
                            )}
                            {categories.map(c => (
                              <option key={c.id} value={c.id.toString()}>{c.name}</option>
                            ))}
                          </select>
                        )}
                      </div>
                      <div>
                        <label className={labelClass}>Cost ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={product.cost}
                          onChange={(e) => updateCatalogueProduct(index, 'cost', e.target.value)}
                          className={inputClass}
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Lead Time</label>
                        <input
                          type="text"
                          value={product.leadTime}
                          onChange={(e) => updateCatalogueProduct(index, 'leadTime', e.target.value)}
                          className={inputClass}
                          placeholder="e.g. 2 weeks, 30 days"
                        />
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardBody>
          </Card>
        </fieldset>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/suppliers')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading}
            isLoading={loading}
          >
            {isEdit ? 'Update Supplier' : pendingNewSupplierImport ? 'Retry Product Import' : 'Create Supplier'}
          </Button>
        </div>
      </form>

      <ExcelImportModal
        isOpen={showProductImportModal}
        title="Import Supplier Catalogue Products"
        schema={supplierCatalogueProductImportSchema}
        rowsLabel="products"
        templateFileName="supplier-catalogue-products-import-template.csv"
        onClose={() => {
          setShowProductImportModal(false);
          if (isEdit) {
            existingSupplierImportRef.current = null;
            setProductImportProgress(null);
          }
        }}
        onConfirm={handleImportCatalogueProducts}
        progressText={isEdit ? productImportProgressText : undefined}
      />
    </div>
  );
};

export default SupplierForm;
